// removed useQueryClient; using trpc utils instead
import { useEffect } from 'react';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { useOrganization } from '@/contexts/OrganizationContext';
import type { SKURulesState } from '../types';
import { trpc } from '@/lib/trpc';
import { useLogger } from '@/lib/logger';

/**
 * Hook for loading and saving SKU rules
 * Handles React Query caching and error states
 */

interface UseSkuRulesReturn {
  rules: SKURulesState;
  loading: boolean;
  error: Error | null;
  saveRules: (rules: SKURulesState) => Promise<void>;
  isSaving: boolean;
}

export function useSkuRules(): UseSkuRulesReturn {
  const { organization } = useOrganization();
  const logger = useLogger();

  // Stable default object to avoid new identity each render when data is undefined
  const DEFAULT_RULES: SKURulesState = {
    doubleSidedPrefixes: [],
    standardGarments: [],
    firstTwoSegments: [],
    wrapperTypes: [],
    passThrough: [],
  };

  // Load rules query
  const utils = trpc.useUtils();
  const { data, isLoading, error } = trpc.skuRules.get.useQuery(undefined, {
    enabled: !!organization?.id,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!data || isLoading) return;

    try {
      const passCount = data.passThrough?.length || 0;
      const hasAF = (data.passThrough || []).includes('AF');
      logger.info('Loaded SKU rules (global)', {
        operation: 'load_sku_rules',
        categories: Object.keys(data),
        passThroughCount: passCount,
        hasAFInPassThrough: hasAF,
      });
    } catch {
      /* noop */
    }
  }, [data, isLoading, logger]);

  // Save rules mutation
  const saveMutation = trpc.skuRules.save.useMutation({
    onSuccess: () => {
      // Invalidate and refetch
      utils.skuRules.get.invalidate();

      toast({
        title: "Changes Saved",
        description: "SKU rules have been updated successfully in the database",
      });

      // Reload page to ensure all systems use the new rules
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError: (error: unknown) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'sku-rules',
          operation: 'save',
        },
      });

      toast({
        title: "Save Failed",
        description: error instanceof Error ? error.message : "Failed to save changes to database. Please try again.",
        variant: "destructive"
      });
    },
  });

  useEffect(() => {
    if (!error || data !== undefined) return;

    Sentry.captureException(error, {
      tags: {
        feature: 'sku-rules',
        operation: 'load',
      },
      level: 'warning',
    });

    toast({
      title: "Database Connection Failed",
      description: "Using default rules. Database features are disabled.",
      variant: "destructive"
    });
  }, [data, error]);

  // Normalize error to Error | null for consumers
  let normalizedError: Error | null = null;
  if (error) {
    normalizedError = error instanceof Error ? error : new Error('Request failed');
  }

  return {
    rules: data ?? DEFAULT_RULES,
    loading: isLoading,
    error: normalizedError,
    saveRules: async (rules: SKURulesState) => { await saveMutation.mutateAsync(rules); },
    isSaving: saveMutation.isPending,
  };
}
